import type { Identity } from '@icp-sdk/core/agent';
import { Principal } from '@icp-sdk/core/principal';
import { backend } from './backend';
import { explain } from './api';
import { icpWallet } from './payments';
import { decimalUnits } from './icpswap';
import { SNSC } from './native-token';

export type WalletAssetId = 'SNSC' | 'ICP';
export interface WalletAsset {
  id: WalletAssetId; symbol: WalletAssetId; name: string; decimals: 8;
  ledger: string; testMode: boolean; balance: bigint | null; fee: bigint | null; error: string;
}
export interface AccountWallet {
  owner: string; address: string | null; addressError: string; assets: Record<WalletAssetId, WalletAsset>;
}
export interface WalletTransfer {
  asset: WalletAssetId; ledger: string; owner: string; to: string;
  amount: string; fee: string; created: string; memo: string; attempted: boolean;
}
const local = () => import.meta.env.VITE_DFX_NETWORK === 'local';
const storageKey = (owner: string) => `soonscreener.send.${owner}`;
const nativeLedger = (asset: WalletAssetId) => asset === 'SNSC' ? SNSC.ledger : SNSC.baseLedger;
const definition = (id: WalletAssetId): WalletAsset => ({ id, symbol: id, name: id === 'SNSC' ? 'Soonscreener' : 'Internet Computer', decimals: 8, ledger: nativeLedger(id), testMode: false, balance: null, fee: null, error: '' });
const accountOwner = (text: string) => {
  const owner = Principal.fromText(text);
  if (owner.isAnonymous()) throw Error('Choose a signed wallet principal.');
  return owner;
};

async function ledgerFor(identity: Identity, id: WalletAssetId) {
  // Production wallet assets are pinned, independent of whether purchases or
  // reactions are enabled. Local fixtures require explicit billing test mode.
  if (!local()) return { ledger: nativeLedger(id), testMode: false };
  const service = await backend(identity), billing = await service.billing_config();
  if (id === 'ICP') {
    if (!billing.test_mode && billing.ledger.toText() !== SNSC.baseLedger) throw Error('The ICP ledger is not verified.');
    return { ledger: billing.ledger.toText(), testMode: billing.test_mode };
  }
  const reactions = await service.reaction_config();
  if (reactions.test_mode && !billing.test_mode) throw Error('Test SNSC requires an explicitly configured local test backend.');
  if (!reactions.test_mode && reactions.ledger.toText() !== SNSC.ledger) throw Error('The SNSC ledger is not verified.');
  return { ledger: reactions.ledger.toText(), testMode: reactions.test_mode };
}

export async function readAccountWallet(identity: Identity): Promise<AccountWallet> {
  const owner = accountOwner(identity.getPrincipal().toText());
  async function read(id: WalletAssetId): Promise<WalletAsset> {
    const asset = definition(id);
    try {
      Object.assign(asset, await ledgerFor(identity, id));
      const ledger = await icpWallet(identity, asset.ledger);
      const [balance, fee] = await Promise.allSettled([ledger.icrc1_balance_of({ owner, subaccount: [] }), ledger.icrc1_fee()]);
      if (balance.status === 'fulfilled') asset.balance = balance.value;
      if (fee.status === 'fulfilled') asset.fee = fee.value;
      asset.error = [balance, fee].flatMap(result => result.status === 'rejected' ? [explain(result.reason)] : []).join(' ');
    } catch (reason) { asset.error = explain(reason); }
    return asset;
  }
  const [snsc, icp, address] = await Promise.all([
    read('SNSC'), read('ICP'),
    backend(identity).then(service => service.wallet_account(owner)).then(bytes => ({ value: Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(''), error: '' })).catch(reason => ({ value: null, error: explain(reason) })),
  ]);
  return { owner: owner.toText(), assets: { SNSC: snsc, ICP: icp }, address: address.value, addressError: address.error };
}

/** Validate persisted public transfer arguments without creating a new intent. */
export function validateWalletTransfer(value: unknown, owner: string): WalletTransfer {
  if (!value || typeof value !== 'object') throw Error('The saved wallet transfer is invalid.');
  const input = value as Record<string, unknown>;
  // Older releases stored only ICP in this same owner-specific key.
  const asset = input.asset ?? 'ICP', memo = input.memo ?? '';
  if ((asset !== 'ICP' && asset !== 'SNSC') || input.owner !== owner || typeof input.attempted !== 'boolean') throw Error('The saved transfer belongs to a different wallet.');
  accountOwner(owner);
  if (typeof input.ledger !== 'string' || Principal.fromText(input.ledger).toText() !== input.ledger) throw Error('The saved transfer ledger is invalid.');
  if (typeof input.to !== 'string' || accountOwner(input.to).toText() !== input.to) throw Error('The saved recipient is invalid.');
  for (const key of ['amount', 'fee', 'created']) {
    if (typeof input[key] !== 'string' || !/^(0|[1-9]\d*)$/.test(input[key])) throw Error('The saved transfer amount or timestamp is invalid.');
  }
  if (BigInt(input.amount as string) <= 0n || BigInt(input.created as string) <= 0n || BigInt(input.created as string) >= 2n ** 64n) throw Error('The saved transfer amount or timestamp is invalid.');
  if (typeof memo !== 'string' || (memo !== '' && !/^[0-9a-f]{64}$/.test(memo))) throw Error('The saved transfer memo is invalid.');
  return { asset, ledger: input.ledger, owner, to: input.to, amount: input.amount as string, fee: input.fee as string, created: input.created as string, memo, attempted: input.attempted };
}

export function readWalletTransfer(owner: string): WalletTransfer | null {
  let saved: string | null;
  try { saved = localStorage.getItem(storageKey(owner)); }
  catch { throw Error('Browser storage is unavailable. Enable it before sending tokens.'); }
  if (saved === null) return null;
  let draft: WalletTransfer;
  try { draft = validateWalletTransfer(JSON.parse(saved), owner); }
  catch { throw Error('The saved wallet transfer could not be verified. Keep it until its ledger result has been checked.'); }
  if (!local() && draft.ledger !== nativeLedger(draft.asset)) throw Error('The saved transfer does not use the verified token ledger.');
  return draft.attempted ? draft : null;
}

export function reviewWalletTransfer(wallet: AccountWallet, asset: WalletAssetId, to: string, quantity: string): WalletTransfer {
  const token = wallet.assets[asset], recipient = accountOwner(to.trim()).toText();
  accountOwner(wallet.owner);
  if (token.balance === null || token.fee === null) throw Error(`Wait for your ${asset} balance and network fee to load.`);
  if (!/^\d+(\.\d{1,8})?$/.test(quantity.trim())) throw Error(`Enter a ${asset} amount with at most eight decimal places.`);
  const value = decimalUnits(quantity.trim(), 8);
  if (value <= 0n || value + token.fee > token.balance) throw Error(`Enter a positive ${asset} amount covered by your balance, including the ledger fee.`);
  const memo = Array.from(crypto.getRandomValues(new Uint8Array(32)), byte => byte.toString(16).padStart(2, '0')).join('');
  return { asset, ledger: token.ledger, owner: wallet.owner, to: recipient, amount: value.toString(), fee: token.fee.toString(), created: (BigInt(Date.now()) * 1_000_000n).toString(), memo, attempted: false };
}

export function walletTransferArgs(draft: WalletTransfer) {
  return {
    from_subaccount: [], to: { owner: accountOwner(draft.to), subaccount: [] },
    amount: BigInt(draft.amount), fee: [BigInt(draft.fee)], created_at_time: [BigInt(draft.created)],
    memo: draft.memo ? [Uint8Array.from(draft.memo.match(/../g)!, byte => parseInt(byte, 16))] : [],
  };
}
const sameIntent = (a: WalletTransfer, b: WalletTransfer) => (['asset', 'ledger', 'owner', 'to', 'amount', 'fee', 'created', 'memo'] as const).every(key => a[key] === b[key]);
function remember(draft: WalletTransfer) {
  try {
    const encoded = JSON.stringify(draft);
    localStorage.setItem(storageKey(draft.owner), encoded);
    if (localStorage.getItem(storageKey(draft.owner)) !== encoded) throw Error('Storage did not retain the transfer.');
  } catch { throw Error('The transfer was not submitted because its original details could not be saved. Enable browser storage and review it again.'); }
}
function clear(draft: WalletTransfer) {
  const saved = readWalletTransfer(draft.owner);
  if (saved && sameIntent(saved, draft)) localStorage.removeItem(storageKey(draft.owner));
}

export async function sendWalletTransfer(identity: Identity, original: WalletTransfer, assertCurrent: () => void): Promise<{ block: bigint }> {
  const draft = validateWalletTransfer(original, identity.getPrincipal().toText());
  assertCurrent();
  if (typeof navigator.locks?.request !== 'function') throw Error('This browser cannot coordinate safe wallet transfers. Use a browser with Web Locks support.');
  return navigator.locks.request(`soonscreener.wallet.send.${draft.owner}`, async () => {
    assertCurrent();
    const saved = readWalletTransfer(draft.owner);
    if (saved && !sameIntent(saved, draft)) throw Error('Another original transfer is awaiting confirmation. Recover it before starting a new transfer.');
    if (draft.attempted && !saved) throw Error('The original transfer record is missing. Check its ledger history before creating another transfer.');
    const config = await ledgerFor(identity, draft.asset); assertCurrent();
    if (config.ledger !== draft.ledger) throw Error('The token ledger changed. The transfer has not been submitted.');
    const ledger = await icpWallet(identity, draft.ledger); assertCurrent();
    const firstAttempt = !saved;
    if (firstAttempt) {
      const [balance, fee] = await Promise.all([ledger.icrc1_balance_of({ owner: identity.getPrincipal(), subaccount: [] }), ledger.icrc1_fee()]); assertCurrent();
      if (fee !== BigInt(draft.fee)) throw Error('The network fee changed. Review the transfer again.');
      if (balance < BigInt(draft.amount) + fee) throw Error(`Your ${draft.asset} balance no longer covers this transfer and its network fee.`);
    }
    const pending = { ...draft, attempted: true };
    remember(pending); assertCurrent();
    const result = await ledger.icrc1_transfer(walletTransferArgs(pending));
    // An old session may finish a previously authorized transfer. Close its
    // matching durable intent, but leave all UI updates to the current owner.
    if ('Ok' in result || ('Err' in result && 'Duplicate' in result.Err)) {
      const block = 'Ok' in result ? result.Ok : (result.Err as { Duplicate: { duplicate_of: bigint } }).Duplicate.duplicate_of;
      clear(pending);
      assertCurrent();
      return { block };
    }
    if (firstAttempt) clear(pending);
    const error = JSON.stringify(result.Err, (_, value) => typeof value === 'bigint' ? value.toString() : value);
    throw Error(`${draft.asset} ledger: ${error}${firstAttempt ? '' : ' The original transfer remains saved; its earlier result is still unconfirmed.'}`);
  });
}
